import React, { useEffect, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import KPITile from "@/components/shared/KPITile";
import {
  Flame, Truck, Clock, FileText, Cpu, CheckCircle2,
  Save, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { downloadReport } from "@/lib/downloadReport";
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";
import api, { extractApiError } from "@/lib/api";

function formatDayLabel(isoDate) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return String(isoDate);
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}

function formatArrivalMinutes(valueInSeconds) {
  const numeric = Number(valueInSeconds);
  if (!Number.isFinite(numeric)) return "—";
  return `${(numeric / 60).toFixed(1)} m`;
}

function formatIncidentType(value) {
  return String(value || "Incident")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function FireReports() {
  const [status, setStatus] = useState("controlled");
  const [damageDesc, setDamageDesc] = useState("");
  const [hazmatAction, setHazmatAction] = useState("CO₂ suppression applied. Coolant barriers placed.");
  const [rescued, setRescued] = useState("2 — transferred to EMS Unit 7");

  const [incidentSummary, setIncidentSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [recentReportsError, setRecentReportsError] = useState("");
  const [analyticsError, setAnalyticsError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadAnalytics = async () => {
      setAnalyticsError("");
      try {
        const [summaryResponse, timeseriesResponse] = await Promise.all([
          api.get("/analytics/incidents/summary", { params: { period: "7d" } }),
          api.get("/analytics/incidents/timeseries", { params: { bucket: "day", incident_type: "FIRE" } }),
        ]);

        if (!isMounted) return;

        setIncidentSummary(summaryResponse?.data?.data || {});
        setChartData(
          Array.isArray(timeseriesResponse?.data?.series)
            ? timeseriesResponse.data.series.map((item) => ({
                day: formatDayLabel(item?.bucket),
                fires: Number(item?.count || 0),
              }))
            : [],
        );
      } catch (err) {
        if (!isMounted) return;
        const { message } = extractApiError(err);
        setAnalyticsError(message || "Failed to load analytics.");
        setIncidentSummary(null);
          setChartData([]);
      }
    };

    loadAnalytics();

    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadRecentReports = async () => {
      try {
        const response = await api.get("/incidents", { params: { status: "RESOLVED", page_size: 5 } });
        if (!isMounted) return;

        const source = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

        setRecentReports(
          source.map((item) => ({
            id: item?.incident_id || item?.id || "—",
            status: item?.status || "—",
            type: formatIncidentType(item?.incident_type || item?.type),
            date: formatDayLabel(item?.event_time || item?.created_at),
            outcome: item?.description || "No description",
          })),
        );
      } catch (err) {
        if (!isMounted) return;
        const { message } = extractApiError(err);
        console.warn("[FireReports] Failed to load recent reports:", message || err?.message || err);
        setRecentReports([]);
        setRecentReportsError(message || "Failed to load recent reports.");
      }
    };

    loadRecentReports();

    return () => {
      isMounted = false;
    };
  }, []);

  const cases = incidentSummary?.cases;
  const deployments = incidentSummary?.deployments;
  const avgArrivalSeconds = incidentSummary?.avg_arrival_seconds;
  const closed = incidentSummary?.closed;

  return (
    <div className="space-y-6" data-testid="fire-reports">
      <PageHeader title="Incident Report" subtitle="Auto-generated sensor logs + manual fields." onDownload={() => {
        downloadReport({
          filename: "fire_incident_report",
          title: "Fire - Incident Report",
          headers: ["Day", "Fires"],
          rows: chartData.map((d) => [d.day, d.fires]),
        });
        toast.success("Report downloaded");
      }} />

      {analyticsError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {analyticsError}
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPITile
          icon={Flame}
          label="Fires this week"
          value={Number.isFinite(Number(cases?.value)) ? Number(cases.value).toLocaleString() : "—"}
          accent="role"
        />
        <KPITile
          icon={Truck}
          label="Trucks deployed"
          value={Number.isFinite(Number(deployments?.value)) ? Number(deployments.value).toLocaleString() : "—"}
          accent="rose"
        />
        <KPITile
          icon={Clock}
          label="Avg arrival"
          value={formatArrivalMinutes(avgArrivalSeconds?.value)}
          accent="amber"
        />
        <KPITile
          icon={FileText}
          label="Reports filed"
          value={Number.isFinite(Number(closed?.value)) ? Number(closed.value).toLocaleString() : "—"}
          accent="indigo"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Main */}
        <div className="space-y-5 xl:col-span-2">


          {/* Manual damage report */}
          <SectionCard title="Manual damage report">
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Damage description
                </label>
                <textarea
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-100"
                  rows={3}
                  placeholder="Describe structural damage, fire spread, injuries…"
                  value={damageDesc}
                  onChange={(e) => setDamageDesc(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Hazmat action taken
                </label>
                <input
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-100"
                  value={hazmatAction}
                  onChange={(e) => setHazmatAction(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Persons rescued
                </label>
                <input
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-100"
                  value={rescued}
                  onChange={(e) => setRescued(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Situation status
                </label>
                <div className="flex gap-2">
                  {["controlled", "partial", "not_controlled"].map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(s)}
                      className={`flex-1 rounded-xl border p-2.5 text-center text-xs font-semibold transition ${
                        status === s && s === "controlled"
                          ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                          : status === s && s === "not_controlled"
                          ? "border-rose-300 bg-rose-50 text-rose-700"
                          : status === s
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                      }`}
                    >
                      {s === "controlled" ? "Controlled" : s === "partial" ? "Partial" : "Not Controlled"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-slate-900 text-white hover:bg-slate-800"
                  onClick={() => toast.info("Draft saved")}
                >
                  <Save size={14} className="mr-1.5" /> Save Draft
                </Button>
                <Button
                  className="flex-1 bg-slate-800 text-white hover:bg-slate-700"
                  onClick={() => toast.success("Report submitted successfully")}
                >
                  <CheckCircle2 size={14} className="mr-1.5" /> Submit Report
                </Button>
              </div>
            </div>
          </SectionCard>

          {/* Chart */}
          <SectionCard title="Fire incidents by day">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="fireGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#BE123C" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#BE123C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Area type="monotone" dataKey="fires" stroke="#BE123C" strokeWidth={2} fill="url(#fireGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>
        </div>

        {/* Side: System output */}
        <div className="space-y-5">


          {/* Incident filter (history) */}
          <SectionCard title="Recent reports">
            <div className="space-y-2.5">
              {recentReportsError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {recentReportsError}
                </div>
              )}
              {recentReports.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
                  <div>
                    <p className="text-xs font-semibold text-slate-900">{r.type}</p>
                    <p className="text-[10px] text-slate-500">{r.status} · {r.date}</p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                    {r.outcome}
                  </span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
